import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { countries } from '../utils/countries';
import './FCLabel.css';

/* ──────────────────────────────────────────────
   Constants
────────────────────────────────────────────── */
const API = process.env.NODE_ENV === 'development'
    ? 'http://localhost:5001/api'
    : '/api';

const SERVICE_TYPES = [
    { code: 'NON-DOCUMENTS', label: 'Non-Document', desc: 'Merchandise, goods, products', icon: '📦' },
    { code: 'DOCUMENTS', label: 'Document', desc: 'Papers, contracts, letters with value', icon: '📄' },
    { code: 'LETTER', label: 'Letter', desc: 'UPS Letter – no commercial value', icon: '✉️' }
];

const EMPTY_ADDR = {
    name: '', attention: '', phone: '', email: '',
    address1: '', address2: '', city: '', state: '', postalCode: '', country: 'US'
};

const EMPTY_DEST = {
    name: '', attention: '', phone: '', email: '',
    address1: '', address2: '', city: '', state: '', postalCode: '', country: 'PK'
};

const EMPTY_PKG = { weight: '', length: '', width: '', height: '', description: '', additionalHandling: false };

// Log API URL on module load
console.log('[RequestFC Module] Loading | API Base URL:', API, '| Environment:', process.env.NODE_ENV);

/* ──────────────────────────────────────────────
   Helpers
────────────────────────────────────────────── */
const getCountryName = (code) => {
    if (!code) return '';
    const c = countries.find(x => x.code === code);
    return c ? c.name : code;
};

const fmtDate = (d) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return '—'; }
};

const fmtDateTime = (d) => {
    if (!d) return '—';
    try {
        return new Date(d).toLocaleDateString('en-GB', {
            day: '2-digit', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    }
    catch { return '—'; }
};

/* ──────────────────────────────────────────────
   ADDRESS BLOCK — defined OUTSIDE the component
────────────────────────────────────────────── */
const FieldHint = ({ text }) => (
    <p style={{ margin: '3px 0 0', fontSize: '0.72rem', color: '#94a3b8', lineHeight: '1.3' }}>{text}</p>
);

const AddrBlock = ({ label, icon, data, onChange, isOrigin }) => (
    <div className="address-column premium-column">
        <div className="column-header">
            <span className="icon">{icon}</span>
            <h3>{label}</h3>
        </div>

        <div className="field-row">
            <div className="field-group">
                <label>Full Name / Company *</label>
                <input
                    className="premium-input"
                    value={data.name}
                    onChange={e => onChange('name', e.target.value)}
                    placeholder="e.g. John Smith or ABC Corp"
                    maxLength={35}
                    required
                />
                <FieldHint text={`Max 35 characters (e.g. "John Smith" or "ABC Logistics")`} />
            </div>
            <div className="field-group">
                <label>Attention / Contact</label>
                <input
                    className="premium-input"
                    value={data.attention}
                    onChange={e => onChange('attention', e.target.value)}
                    placeholder="Contact person"
                    maxLength={35}
                />
                <FieldHint text="Max 35 characters — the contact person's name" />
            </div>
        </div>

        <div className="field-row">
            <div className="field-group">
                <label>Email Address</label>
                <input
                    className="premium-input"
                    type="email"
                    value={data.email}
                    onChange={e => onChange('email', e.target.value)}
                    placeholder="email@example.com"
                />
                <FieldHint text="Standard email format (e.g. name@company.com)" />
            </div>
            <div className="field-group">
                <label>Phone Number</label>
                <input
                    className="premium-input"
                    type="tel"
                    value={data.phone}
                    onChange={e => onChange('phone', e.target.value)}
                    placeholder="+1 555 000 0000"
                    maxLength={15}
                />
                <FieldHint text="Digits only, max 15 digits (e.g. +1 555 000 0000)" />
            </div>
        </div>

        <div className="field-row">
            <div className="field-group">
                <label>Country *</label>
                <select
                    className="premium-input select-input"
                    value={data.country}
                    onChange={e => onChange('country', e.target.value)}
                >
                    {countries.map(c => (
                        <option key={c.code} value={c.code}>{c.name} ({c.code})</option>
                    ))}
                </select>
            </div>
        </div>

        <div className="field-row">
            <div className="field-group">
                <label>Address Line 1 *</label>
                <input
                    className="premium-input"
                    value={data.address1}
                    onChange={e => onChange('address1', e.target.value)}
                    placeholder="Street address, P.O. box, company name"
                    maxLength={35}
                    required
                />
                <FieldHint text={`Max 4 words · max 35 chars (e.g. "123 Main Street Suite 5")`} />
            </div>
        </div>

        <div className="field-row">
            <div className="field-group">
                <label>Address Line 2 (Optional)</label>
                <input
                    className="premium-input"
                    value={data.address2}
                    onChange={e => onChange('address2', e.target.value)}
                    placeholder="Apartment, suite, unit, building, floor, etc."
                    maxLength={35}
                />
                <FieldHint text={`Max 35 chars — apt/suite/unit number (e.g. "Apt 4B")`} />
            </div>
        </div>

        <div className="city-zip-state-row">
            <div className="field-group" style={{ flex: 2 }}>
                <label>City *</label>
                <input
                    className="premium-input"
                    value={data.city}
                    onChange={e => onChange('city', e.target.value)}
                    placeholder="City"
                    maxLength={30}
                    required
                />
                <FieldHint text={`Max 30 chars (e.g. "New York")`} />
            </div>
            <div className="field-group">
                <label>ZIP / Postal *</label>
                <input
                    className="premium-input"
                    value={data.postalCode}
                    onChange={e => onChange('postalCode', e.target.value)}
                    placeholder="12345"
                    maxLength={10}
                    required
                />
                <FieldHint text="US: 5 digits (12345) · Intl: varies" />
            </div>
            <div className="field-group">
                <label>State</label>
                <input
                    className="premium-input"
                    value={data.state}
                    onChange={e => onChange('state', e.target.value)}
                    placeholder="CA"
                    maxLength={2}
                />
                <FieldHint text="2-letter abbreviation (e.g. CA, NY, TX)" />
            </div>
        </div>
    </div>
);

/* ──────────────────────────────────────────────
   Request Detail Modal
────────────────────────────────────────────── */
const RequestDetailModal = ({ request, onClose, onApprove, onReject, onVoid, isApprover, loading }) => {
    if (!request) return null;

    const statusBadge = {
        'pending':  { color: '#f59e0b', label: '⏳ Pending' },
        'approved': { color: '#10b981', label: '✅ Approved' },
        'rejected': { color: '#ef4444', label: '❌ Rejected' },
        'voided':   { color: '#6b7280', label: '🚫 Voided' }
    };

    const status = statusBadge[request.status] || { color: '#6b7280', label: '?' };

    return (
        <div className="fc-form-overlay" onClick={onClose}>
            <div className="fc-result-modal" onClick={e => e.stopPropagation()} style={{ maxHeight: '90vh', overflowY: 'auto' }}>
                <div className="fc-result-head">
                    <h3>🏷️ FC Label Request #{request._id?.substring(0, 8)}</h3>
                    <button className="fc-close-btn" onClick={onClose}>✕</button>
                </div>

                <div style={{ padding: '0 22px 12px' }}>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '12px' }}>
                        <span style={{ color: status.color, fontWeight: 600, fontSize: '0.95rem' }}>
                            {status.label}
                        </span>
                        <span style={{ color: '#64748b', fontSize: '0.85rem' }}>
                            {fmtDateTime(request.createdAt)}
                        </span>
                    </div>

                    {/* Requester Info */}
                    <div style={{ background: '#f1f5f9', padding: '12px', borderRadius: '8px', marginBottom: '12px' }}>
                        <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '4px' }}>Requested by</div>
                        <div style={{ fontWeight: 600, color: '#1e293b' }}>{request.requester}</div>
                        <div style={{ fontSize: '0.85rem', color: '#64748b' }}>{request.requesterEmail}</div>
                    </div>

                    {request.notes && (
                        <div style={{ background: '#fef3c7', padding: '12px', borderRadius: '8px', marginBottom: '12px', borderLeft: '4px solid #f59e0b' }}>
                            <div style={{ fontSize: '0.85rem', color: '#92400e', marginBottom: '4px', fontWeight: 600 }}>Notes</div>
                            <div style={{ fontSize: '0.9rem', color: '#78350f' }}>{request.notes}</div>
                        </div>
                    )}

                    {/* Shipment Details */}
                    <div style={{ marginBottom: '12px' }}>
                        <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: '8px', fontSize: '0.95rem' }}>Shipment Details</div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                            <div style={{ background: '#f8fafc', padding: '10px', borderRadius: '6px' }}>
                                <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600, marginBottom: '4px' }}>ACCOUNT</div>
                                <div style={{ fontFamily: 'monospace', fontSize: '0.9rem', color: '#1e293b' }}>{request.accountNumber}</div>
                            </div>
                            <div style={{ background: '#f8fafc', padding: '10px', borderRadius: '6px' }}>
                                <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600, marginBottom: '4px' }}>SERVICE TYPE</div>
                                <div style={{ fontSize: '0.9rem', color: '#1e293b' }}>{request.serviceType === 'NON-DOCUMENTS' ? 'Non-Document' : request.serviceType === 'DOCUMENTS' ? 'Document' : 'Letter'}</div>
                            </div>
                        </div>

                        {/* Tracking number if approved */}
                        {request.trackingNumber && (
                            <div style={{ background: '#f0fdf4', padding: '10px', borderRadius: '6px', marginBottom: '10px', border: '1px solid #bbf7d0' }}>
                                <div style={{ fontSize: '0.75rem', color: '#166534', fontWeight: 600, marginBottom: '4px' }}>TRACKING NUMBER</div>
                                <div style={{ fontFamily: 'monospace', fontSize: '1rem', color: '#166534', fontWeight: 700 }}>{request.trackingNumber}</div>
                            </div>
                        )}

                        {/* Ship From */}
                        <div style={{ background: '#f8fafc', padding: '10px', borderRadius: '6px', marginBottom: '10px' }}>
                            <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600, marginBottom: '4px' }}>SHIP FROM (USA CUSTOMER)</div>
                            <div style={{ fontWeight: 600, color: '#1e293b', fontSize: '0.9rem' }}>{request.shipFrom?.name}</div>
                            <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
                                {request.shipFrom?.address1}
                                {request.shipFrom?.address2 && `, ${request.shipFrom.address2}`}
                            </div>
                            <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
                                {request.shipFrom?.city}, {request.shipFrom?.state} {request.shipFrom?.postalCode}
                            </div>
                            <div style={{ fontSize: '0.85rem', color: '#64748b' }}>{getCountryName(request.shipFrom?.country)}</div>
                        </div>

                        {/* Ship To */}
                        <div style={{ background: '#f8fafc', padding: '10px', borderRadius: '6px', marginBottom: '10px' }}>
                            <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600, marginBottom: '4px' }}>SHIP TO (PAKISTAN DESTINATION)</div>
                            <div style={{ fontWeight: 600, color: '#1e293b', fontSize: '0.9rem' }}>{request.shipTo?.name}</div>
                            <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
                                {request.shipTo?.address1}
                                {request.shipTo?.address2 && `, ${request.shipTo.address2}`}
                            </div>
                            <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
                                {request.shipTo?.city}, {request.shipTo?.state} {request.shipTo?.postalCode}
                            </div>
                            <div style={{ fontSize: '0.85rem', color: '#64748b' }}>{getCountryName(request.shipTo?.country)}</div>
                        </div>

                        {/* Packages */}
                        <div style={{ background: '#f8fafc', padding: '10px', borderRadius: '6px' }}>
                            <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600, marginBottom: '6px' }}>PACKAGES</div>
                            {request.packages?.map((pkg, idx) => (
                                <div key={idx} style={{ fontSize: '0.85rem', color: '#1e293b', marginBottom: idx < request.packages.length - 1 ? '6px' : 0 }}>
                                    <span>📦 {pkg.weight} kg</span>
                                    {pkg.length && <span>, {pkg.length}×{pkg.width}×{pkg.height} cm</span>}
                                    {pkg.description && <span>, {pkg.description}</span>}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Approver action buttons — Pending */}
                    {request.status === 'pending' && isApprover && (
                        <div style={{ display: 'flex', gap: '10px', marginTop: '16px', borderTop: '1px solid #e2e8f0', paddingTop: '16px' }}>
                            <button
                                onClick={() => onApprove(request._id)}
                                disabled={loading}
                                style={{
                                    flex: 1, padding: '10px 14px', borderRadius: '6px',
                                    background: '#10b981', color: '#fff', border: 'none',
                                    cursor: loading ? 'not-allowed' : 'pointer', fontWeight: 600,
                                    opacity: loading ? 0.6 : 1
                                }}
                            >
                                ✅ Approve & Create Label
                            </button>
                            <button
                                onClick={() => onReject(request._id)}
                                disabled={loading}
                                style={{
                                    flex: 1, padding: '10px 14px', borderRadius: '6px',
                                    background: '#ef4444', color: '#fff', border: 'none',
                                    cursor: loading ? 'not-allowed' : 'pointer', fontWeight: 600,
                                    opacity: loading ? 0.6 : 1
                                }}
                            >
                                ❌ Reject Request
                            </button>
                        </div>
                    )}

                    {/* Approver void button — Approved */}
                    {request.status === 'approved' && isApprover && (
                        <div style={{ marginTop: '16px', borderTop: '1px solid #e2e8f0', paddingTop: '16px' }}>
                            <button
                                onClick={() => onVoid(request._id)}
                                disabled={loading}
                                style={{
                                    width: '100%', padding: '10px 14px', borderRadius: '6px',
                                    background: '#6b7280', color: '#fff', border: 'none',
                                    cursor: loading ? 'not-allowed' : 'pointer', fontWeight: 600,
                                    opacity: loading ? 0.6 : 1
                                }}
                            >
                                🚫 Void Label
                            </button>
                            <p style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '6px', textAlign: 'center' }}>
                                Voiding will cancel this shipment with UPS and notify the requester by email.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

/* ══════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════ */
const RequestFC = ({ onBack, userAccounts = [], importAccounts: userImportAccounts = [], isSuperUser = false, currentUser = '', currentUserEmail = '', companySlug = '' }) => {

    /* ── Tab & View state ── */
    const [activeTab, setActiveTab] = useState('create'); // 'create' | 'requests' | 'approved' | 'history'
    const [viewMode, setViewMode] = useState('list'); // 'list' | 'create'

    /* ── Create form state ── */
    const [accounts, setAccounts] = useState([]);
    const [selectedAccount, setSelectedAccount] = useState('');
    const [serviceType, setServiceType] = useState('NON-DOCUMENTS');
    const [shipFrom, setShipFrom] = useState({ ...EMPTY_ADDR });
    const [shipTo, setShipTo] = useState({ ...EMPTY_DEST });
    const [packages, setPackages] = useState([{ ...EMPTY_PKG }]);
    const [generalDescription, setGeneralDescription] = useState('');
    const [reference1, setReference1] = useState('');
    const [notes, setNotes] = useState('');
    const [declaredValue, setDeclaredValue] = useState('');

    /* ── List state ── */
    const [requests, setRequests] = useState([]);
    const [approved, setApproved] = useState([]);
    const [history, setHistory] = useState([]);
    const [loadingList, setLoadingList] = useState(false);

    /* ── Modal state ── */
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);

    /* ── UI state ── */
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    /* ── Approver/Requester info ── */
    const [userInfo, setUserInfo] = useState(null);

    /* ── Request tracking refs (prevent duplicate requests and request storms) ── */
    const fetchInProgressRef = useRef({});
    const retryCountRef = useRef({});
    const MAX_RETRIES = 3;
    const MAX_CONCURRENT_REQUESTS = 5;

    /* ── Stable onChange handlers ── */
    const handleShipFromChange = useCallback((field, val) => {
        setShipFrom(prev => ({ ...prev, [field]: val }));
    }, []);

    const handleShipToChange = useCallback((field, val) => {
        setShipTo(prev => ({ ...prev, [field]: val }));
    }, []);

    /* Helper: Make request with deduplication and backoff */
    const makeRequest = useCallback(async (key, urlOrConfig, isGet = true) => {
        // Prevent duplicate simultaneous requests
        if (fetchInProgressRef.current[key]) {
            console.log(`[RequestFC] Request "${key}" already in progress, skipping duplicate`);
            return null;
        }

        // Check if too many requests are pending
        const activeRequests = Object.values(fetchInProgressRef.current).filter(Boolean).length;
        if (activeRequests >= MAX_CONCURRENT_REQUESTS) {
            console.warn(`[RequestFC] Too many concurrent requests (${activeRequests}), delaying "${key}"`);
            await new Promise(resolve => setTimeout(resolve, 500));
        }

        fetchInProgressRef.current[key] = true;
        try {
            const res = isGet ? await axios.get(urlOrConfig) : await axios.post(urlOrConfig, {});
            retryCountRef.current[key] = 0; // Reset retry count on success
            return res;
        } catch (err) {
            const isInsufficientResources = err.message === 'Network Error' ||
                err.code === 'ECONNREFUSED' ||
                err.toString().includes('ERR_INSUFFICIENT_RESOURCES');

            if (isInsufficientResources && (retryCountRef.current[key] || 0) < MAX_RETRIES) {
                const retryCount = (retryCountRef.current[key] || 0) + 1;
                const backoffMs = Math.pow(2, retryCount) * 1000; // Exponential backoff: 2s, 4s, 8s

                console.warn(`[RequestFC] Request "${key}" failed with resource error, retrying in ${backoffMs}ms (attempt ${retryCount}/${MAX_RETRIES})`);
                retryCountRef.current[key] = retryCount;

                await new Promise(resolve => setTimeout(resolve, backoffMs));

                // Clear the in-progress flag and retry
                fetchInProgressRef.current[key] = false;
                return makeRequest(key, urlOrConfig, isGet);
            }

            throw err;
        } finally {
            fetchInProgressRef.current[key] = false;
        }
    }, []);

    /* ──────── Fetch accounts ──────── */
    const fetchAccounts = useCallback(async () => {
        try {
            console.log('[RequestFC] Fetching import-only accounts...');
            const res = await makeRequest('fetch-import-accounts', `${API}/settings/import-tariffs`, true);

            if (res && res.data.success) {
                const importAccounts = [...new Set(res.data.tariffs.map(t => t.account))];

                let finalAccounts = [];
                if (!isSuperUser && userImportAccounts?.length > 0 && !userImportAccounts.includes('all')) {
                    // Filter user's assigned accounts to only include those that are also in import tariffs
                    finalAccounts = userImportAccounts.filter(acc => importAccounts.includes(acc));
                    // Fallback to import accounts if filtering leaves 0 (optional design choice)
                    if (finalAccounts.length === 0 && userImportAccounts.includes('all')) finalAccounts = importAccounts;
                } else {
                    finalAccounts = importAccounts;
                }

                setAccounts(finalAccounts);
                if (finalAccounts.length === 1) setSelectedAccount(finalAccounts[0]);
                console.log('[RequestFC] Import accounts loaded:', finalAccounts);
            }
        } catch (err) {
            console.warn('[RequestFC] Failed to fetch import accounts:', err.message);
        }
    }, [isSuperUser, userAccounts, makeRequest]);

    /* ──────── Fetch user info (for approver/requester) ──────── */
    const fetchUserInfo = useCallback(async () => {
        try {
            console.log('[RequestFC] Fetching user info from:', `${API}/user/info`);
            const res = await makeRequest('fetch-user-info', `${API}/user/info`, true);
            if (!res) return; // Duplicate request skipped

            console.log('[RequestFC] User info success:', res.data);
            if (res.data.success) {
                setUserInfo(res.data.user);
                setError(''); // Clear any previous errors
            } else {
                const msg = res.data.message || 'Failed to fetch user info';
                console.warn('[RequestFC] User info failed:', msg);
            }
        } catch (err) {
            const status = err.response?.status || 'no-status';
            const errMsg = err.response?.data?.message || err.message || 'Unknown network error';
            console.error('[RequestFC] User info error:', {
                status,
                message: errMsg,
                response: err.response?.data,
                fullError: err.toString()
            });
            // Silently fail - don't show error to user for this background fetch
        }
    }, [makeRequest]);

    /* ──────── Fetch requests (pending) ──────── */
    const fetchRequests = useCallback(async () => {
        setLoadingList(true);
        try {
            const url = `${API}/request-fc/list?status=pending`;
            console.log('[RequestFC] Fetching pending requests from:', url);
            const res = await makeRequest('fetch-requests-pending', url, true);
            if (!res) {
                setLoadingList(false);
                return; // Duplicate request skipped
            }

            console.log('[RequestFC] Pending requests response:', { status: res.status, data: res.data });
            if (res.data.success) {
                setRequests(res.data.requests || []);
                setError(''); // Clear error on success
            } else {
                const msg = res.data.message || 'Failed to fetch pending requests';
                console.warn('[RequestFC] Pending requests API returned error:', msg);
                setError(`Pending Requests: ${msg}`);
            }
        } catch (err) {
            const status = err.response?.status || err.code || 'unknown';
            const errMsg = err.response?.data?.message || err.message || 'Network error';
            const url = err.config?.url || 'unknown URL';
            console.error('[RequestFC] Pending requests fetch error:', {
                status,
                url,
                message: errMsg,
                responseData: err.response?.data,
                isServer: err.response ? 'yes (got response)' : 'no (no response)',
                errorType: err.name,
                toString: err.toString()
            });
            setError(`❌ Cannot load requests: ${errMsg} (Status: ${status})`);
            setRequests([]);
        } finally {
            setLoadingList(false);
        }
    }, [makeRequest]);

    /* ──────── Fetch approved ──────── */
    const fetchApproved = useCallback(async () => {
        setLoadingList(true);
        try {
            console.log('[RequestFC] Fetching approved requests');
            const res = await makeRequest('fetch-approved', `${API}/request-fc/list?status=approved`, true);
            if (!res) {
                setLoadingList(false);
                return;
            }

            console.log('[RequestFC] Approved requests response:', res.data);
            if (res.data.success) {
                setApproved(res.data.requests || []);
            } else {
                setError(res.data.message || 'Failed to fetch approved requests');
            }
        } catch (err) {
            const errMsg = err.response?.data?.message || err.message || 'Failed to fetch approved';
            console.error('[RequestFC] Failed to fetch approved:', err.response?.data || err.message);
            setError(`Error: ${errMsg}`);
            setApproved([]);
        } finally {
            setLoadingList(false);
        }
    }, [makeRequest]);

    /* ──────── Fetch history (all) ──────── */
    const fetchHistory = useCallback(async () => {
        setLoadingList(true);
        try {
            console.log('[RequestFC] Fetching all history');
            const res = await makeRequest('fetch-history', `${API}/request-fc/list`, true);
            if (!res) {
                setLoadingList(false);
                return;
            }

            console.log('[RequestFC] History response:', res.data);
            if (res.data.success) {
                setHistory(res.data.requests || []);
            } else {
                setError(res.data.message || 'Failed to fetch history');
            }
        } catch (err) {
            const errMsg = err.response?.data?.message || err.message || 'Failed to fetch history';
            console.error('[RequestFC] Failed to fetch history:', err.response?.data || err.message);
            setError(`Error: ${errMsg}`);
            setHistory([]);
        } finally {
            setLoadingList(false);
        }
    }, [makeRequest]);

    // Initialize on mount - fetch accounts and user info once
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        console.log('[RequestFC] Component initialized | User:', currentUserEmail, '| Company:', companySlug);
        console.log('[RequestFC] Authorized header check:', axios.defaults.headers.common['Authorization'] ? 'Present ✅' : 'Missing ❌');

        // Only fetch once on mount
        fetchAccounts();
        fetchUserInfo();

        // Quick API connectivity test
        (async () => {
            try {
                console.log('[RequestFC] Testing API connectivity...');
                const testRes = await axios.get(`${API}/test`);
                console.log('[RequestFC] API test successful:', testRes.data);
            } catch (err) {
                console.error('[RequestFC] API test failed:', {
                    status: err.response?.status,
                    message: err.message,
                    url: `${API}/test`
                });
            }
        })();
    }, []); // Empty dependency array - run only once on mount

    useEffect(() => {
        if (activeTab === 'requests') {
            setError('');
            fetchRequests();
        }
        else if (activeTab === 'approved') {
            setError('');
            fetchApproved();
        }
        else if (activeTab === 'history') {
            setError('');
            fetchHistory();
        }
    }, [activeTab, fetchRequests, fetchApproved, fetchHistory]);

    /* ──────── Package helpers ──────── */
    const addPkg = () => setPackages(prev => [...prev, { ...EMPTY_PKG }]);
    const removePkg = (i) => setPackages(prev => prev.filter((_, idx) => idx !== i));
    const updatePkg = (i, field, val) =>
        setPackages(prev => prev.map((p, idx) => idx === i ? { ...p, [field]: val } : p));

    /* ──────── Validate ──────── */
    const validate = () => {
        if (!selectedAccount) return 'Please select an account.';
        if (!shipFrom.name) return 'Ship From: Name is required.';
        if (!shipFrom.address1) return 'Ship From: Address is required.';
        if (!shipFrom.city) return 'Ship From: City is required.';
        if (!shipFrom.country) return 'Ship From: Country is required.';
        if (!shipTo.name) return 'Ship To: Name is required.';
        if (!shipTo.address1) return 'Ship To: Address is required.';
        if (!shipTo.city) return 'Ship To: City is required.';
        if (!shipTo.country) return 'Ship To: Country is required.';
        if (!generalDescription.trim()) return 'General description is required.';
        for (let i = 0; i < packages.length; i++) {
            const p = packages[i];
            if (!p.weight) return `Package ${i + 1}: Weight is required.`;
            if (serviceType !== 'LETTER' && (!p.length || !p.width || !p.height))
                return `Package ${i + 1}: Dimensions (L×W×H) are required.`;
        }
        return null;
    };

    /* ──────── Create request ──────── */
    const handleCreateRequest = async () => {
        setError('');
        setSuccess('');
        const err = validate();
        if (err) { setError(err); return; }
        setLoading(true);
        try {
            const payload = {
                accountNumber: selectedAccount,
                serviceType,
                shipFrom,
                shipTo,
                packages: packages.map(p => ({ ...p, weightUnit: 'KGS', dimUnit: 'CM', additionalHandling: !!p.additionalHandling })),
                generalDescription,
                reference1,
                declaredValue,
                notes,
                requester: currentUser,
                requesterEmail: currentUserEmail,
                companySlug,
                createdAt: new Date().toISOString()
            };
            const res = await axios.post(`${API}/request-fc/create`, payload);
            if (res.data.success) {
                setSuccess('✅ Request submitted for approval. Email sent to approver.');
                resetForm();
                setTimeout(() => { setSuccess(''); }, 3000);
            } else {
                setError(res.data.message || 'Failed to create request.');
            }
        } catch (e) {
            setError(e.response?.data?.message || e.message || 'Failed to create request.');
        } finally {
            setLoading(false);
        }
    };

    /* ──────── Approve request ──────── */
    const handleApproveRequest = async (requestId) => {
        setLoading(true);
        try {
            const res = await axios.post(`${API}/request-fc/approve`, { requestId });
            if (res.data.success) {
                setSuccess('✅ Request approved. Label created and email sent.');
                setShowDetailModal(false);
                fetchRequests();
                setTimeout(() => { setSuccess(''); }, 3000);
            } else {
                setError(res.data.message || 'Failed to approve request.');
            }
        } catch (e) {
            setError(e.response?.data?.message || e.message || 'Failed to approve request.');
        } finally {
            setLoading(false);
        }
    };

    /* ──────── Reject request ──────── */
    const handleRejectRequest = async (requestId) => {
        if (!window.confirm('Are you sure you want to reject this request?')) return;
        setLoading(true);
        try {
            const res = await axios.post(`${API}/request-fc/reject`, { requestId });
            if (res.data.success) {
                setSuccess('❌ Request rejected. Email sent to requester.');
                setShowDetailModal(false);
                fetchRequests();
                setTimeout(() => { setSuccess(''); }, 3000);
            } else {
                setError(res.data.message || 'Failed to reject request.');
            }
        } catch (e) {
            setError(e.response?.data?.message || e.message || 'Failed to reject request.');
        } finally {
            setLoading(false);
        }
    };

    /* ──────── Void request (approver only) ──────── */
    const handleVoidRequest = async (requestId) => {
        if (!window.confirm('Are you sure you want to VOID this label? This will cancel the shipment with UPS and notify the requester.')) return;
        setLoading(true);
        try {
            const res = await axios.post(`${API}/request-fc/void`, { requestId });
            if (res.data.success) {
                setSuccess(`🚫 Label voided. ${res.data.voidedWithUps ? 'UPS void confirmed.' : 'Voided in system (UPS void may need manual follow-up).'}`);
                setShowDetailModal(false);
                fetchApproved();
                setTimeout(() => { setSuccess(''); }, 5000);
            } else {
                setError(res.data.message || 'Failed to void label.');
            }
        } catch (e) {
            setError(e.response?.data?.message || e.message || 'Failed to void label.');
        } finally {
            setLoading(false);
        }
    };

    /* ──────── Reset form ──────── */
    const resetForm = () => {
        setShipFrom({ ...EMPTY_ADDR });
        setShipTo({ ...EMPTY_DEST });
        setPackages([{ ...EMPTY_PKG }]);
        setGeneralDescription('');
        setReference1('');
        setDeclaredValue('');
        setNotes('');
        setError('');
        if (accounts.length !== 1) setSelectedAccount('');
    };

    const isApprover = userInfo?.isApprover || false;
    const isRequester = userInfo?.isRequester || false;

    /* ══════════════════════════════════════════════
       RENDER — CREATE VIEW
    ══════════════════════════════════════════════ */
    if (viewMode === 'create') {
        return (
            <div className="fc-container">
                <div className="fc-header">
                    <button className="fc-back-btn" onClick={() => setViewMode('list')}>← Back to List</button>
                    <div className="fc-header-title">
                        <div className="fc-header-icon">📋</div>
                        <div>
                            <h1>Request FC Label</h1>
                            <p>Submit a label request for approval before creating the shipment</p>
                        </div>
                    </div>
                </div>

                <div className="fc-content premium-suite" style={{ maxWidth: '1000px', margin: '0 auto' }}>
                    {error && <div className="fc-alert error" style={{ margin: '12px 0' }}>⚠️ {error}</div>}
                    {success && <div className="fc-alert success" style={{ margin: '12px 0' }}>✅ {success}</div>}

                    {/* Account & Service */}
                    <div className="form-section-header">
                        <h3>1. Request Basics</h3>
                        <p>Select billing account and shipment type for this request</p>
                    </div>

                    <div className="fc-section-premium">
                        <div className="field-row">
                            <div className="field-group">
                                <label>Billing Account *</label>
                                <select className="premium-input" value={selectedAccount} onChange={e => setSelectedAccount(e.target.value)}>
                                    <option value="">-- Select UPS Account --</option>
                                    {accounts.map(acc => (
                                        <option key={acc} value={acc}>{acc}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <label className="section-subtitle" style={{ marginTop: '20px', display: 'block' }}>Select Service Classification</label>
                        <div className="fc-service-grid-premium">
                            {SERVICE_TYPES.map(st => (
                                <div
                                    key={st.code}
                                    className={`fc-service-card-premium ${serviceType === st.code ? 'selected' : ''}`}
                                    onClick={() => setServiceType(st.code)}
                                >
                                    <div className="svc-icon">{st.icon}</div>
                                    <div className="svc-info">
                                        <div className="svc-label">{st.label}</div>
                                        <div className="svc-desc">{st.desc}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Ship From */}
                    <div className="form-section-header">
                        <h3>2. Shipment Addresses</h3>
                        <p>Define the original location and the final destination</p>
                    </div>

                    <div className="address-split-container" style={{ marginBottom: '25px' }}>
                        <AddrBlock
                            label="Ship From (Customer/Origin)"
                            icon="📦"
                            data={shipFrom}
                            onChange={handleShipFromChange}
                        />

                        <div className="address-divider"></div>

                        <AddrBlock
                            label="Ship To (You/Destination)"
                            icon="🎯"
                            data={shipTo}
                            onChange={handleShipToChange}
                        />
                    </div>

                    {/* Packages */}
                    <div className="form-section-header">
                        <h3>3. Package Information</h3>
                        <p>Enter parcel specifications for the shipment</p>
                    </div>

                    <div className="fc-section-premium">
                        <div className="package-list premium-package-list">
                            {packages.map((pkg, i) => (
                                <div key={i} className="package-card-premium">
                                    <div className="package-card-header">
                                        <span className="pkg-badge">Package #{i + 1}</span>
                                        {packages.length > 1 && (
                                            <button className="remove-pkg-btn" onClick={() => removePkg(i)}>✕</button>
                                        )}
                                    </div>
                                    <div className="package-grid-row">
                                        <div className="field-group">
                                            <label>Weight (KG)*</label>
                                            <input
                                                className="premium-input"
                                                type="number" min="0" step="0.1"
                                                placeholder="0.0"
                                                value={pkg.weight}
                                                onChange={e => updatePkg(i, 'weight', e.target.value)}
                                            />
                                            <FieldHint text="Numeric only, in KG (e.g. 1.5 or 10)" />
                                        </div>
                                        <div className="field-group">
                                            <label>Length (CM)*</label>
                                            <input
                                                className="premium-input"
                                                type="number" min="0" step="1"
                                                disabled={serviceType === 'LETTER'}
                                                value={pkg.length}
                                                onChange={e => updatePkg(i, 'length', e.target.value)}
                                            />
                                            <FieldHint text="Max 270 cm per side" />
                                        </div>
                                        <div className="field-group">
                                            <label>Width (CM)*</label>
                                            <input
                                                className="premium-input"
                                                type="number" min="0" step="1"
                                                disabled={serviceType === 'LETTER'}
                                                value={pkg.width}
                                                onChange={e => updatePkg(i, 'width', e.target.value)}
                                            />
                                            <FieldHint text="Max 270 cm per side" />
                                        </div>
                                        <div className="field-group">
                                            <label>Height (CM)*</label>
                                            <input
                                                className="premium-input"
                                                type="number" min="0" step="1"
                                                disabled={serviceType === 'LETTER'}
                                                value={pkg.height}
                                                onChange={e => updatePkg(i, 'height', e.target.value)}
                                            />
                                            <FieldHint text="Max 270 cm per side" />
                                        </div>
                                    </div>
                                    <div className="field-row" style={{ marginTop: '10px' }}>
                                        <div className="field-group">
                                            <label>Contents Description</label>
                                            <input
                                                className="premium-input"
                                                type="text"
                                                placeholder="What is inside this package?"
                                                value={pkg.description}
                                                onChange={e => updatePkg(i, 'description', e.target.value)}
                                                maxLength={50}
                                            />
                                            <FieldHint text="Max 50 chars (e.g. Clothing, Electronics, Documents)" />
                                        </div>
                                    </div>
                                    <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center' }}>
                                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.82rem', color: '#334155', cursor: 'pointer', fontWeight: 600 }}>
                                            <input
                                                type="checkbox"
                                                checked={!!pkg.additionalHandling}
                                                onChange={e => updatePkg(i, 'additionalHandling', e.target.checked)}
                                                style={{ width: '16px', height: '16px', accentColor: '#2563eb', cursor: 'pointer' }}
                                            />
                                            Additional Handling / Irregular Packaging (AHC)
                                        </label>
                                    </div>
                                </div>
                            ))}
                        </div>
                        {packages.length < 30 && (
                            <button className="add-pkg-btn-premium" onClick={addPkg} style={{ marginTop: '15px' }}>
                                + Add Another Package
                            </button>
                        )}
                    </div>

                    {/* Additional Details */}
                    <div className="form-section-header">
                        <h3>4. Request Details</h3>
                        <p>Overall description and internal notes for the approver</p>
                    </div>

                    <div className="fc-section-premium">
                        <div className="field-row">
                            <div className="field-group">
                                <label>General Description *</label>
                                <textarea
                                    className="premium-input"
                                    value={generalDescription}
                                    onChange={e => setGeneralDescription(e.target.value)}
                                    placeholder="e.g. Personal effects, sample clothing..."
                                    rows={2}
                                    maxLength={100}
                                />
                                <FieldHint text="Max 100 chars — describes the overall shipment content (e.g. Personal Effects)" />
                            </div>
                        </div>
                        <div className="field-row">
                            <div className="field-group" style={{ flex: 1 }}>
                                <label>Internal Approval Note</label>
                                <textarea
                                    className="premium-input"
                                    value={notes}
                                    onChange={e => setNotes(e.target.value)}
                                    placeholder="Message for the approver (optional)"
                                    rows={2}
                                    maxLength={500}
                                />
                                <FieldHint text="Max 500 chars — message visible to the approver only" />
                            </div>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="fc-footer-actions-premium">
                        <button className="premium-cancel-btn" onClick={() => { resetForm(); setViewMode('list'); }}>
                            Cancel
                        </button>
                        <button
                            className="premium-submit-btn"
                            onClick={handleCreateRequest}
                            disabled={loading}
                        >
                            {loading ? (
                                <><span className="spinner-small"></span> Sending Request...</>
                            ) : '🚀 Submit Request for Approval'}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    /* ══════════════════════════════════════════════
       RENDER — LIST VIEW WITH TABS
    ══════════════════════════════════════════════ */
    return (
        <div className="fc-container">
            <div className="fc-header">
                <button className="fc-back-btn" onClick={onBack}>← Back</button>
                <div className="fc-header-title">
                    <div className="fc-header-icon">📋</div>
                    <div>
                        <h1>FC Label Requests</h1>
                        <p>Request, approve, and manage FC labels with email notifications</p>
                    </div>
                </div>
                <button className="fc-new-btn"
                    onClick={() => setViewMode('create')}
                    disabled={!isApprover && !isRequester}
                    title={(!isApprover && !isRequester) ? 'You do not have the Requester role. Contact your admin to enable FC request creation.' : 'Create a new FC label request'}
                    style={{ opacity: (!isApprover && !isRequester) ? 0.5 : 1, cursor: (!isApprover && !isRequester) ? 'not-allowed' : 'pointer' }}
                >
                    + New Request
                </button>
            </div>

            <div className="fc-content">
                {error && <div className="fc-alert error" style={{ margin: '12px' }}>⚠️ {error}</div>}
                {success && <div className="fc-alert success" style={{ margin: '12px' }}>✅ {success}</div>}

                {/* Tab Navigation */}
                <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', borderBottom: '2px solid #e2e8f0', paddingBottom: '12px' }}>
                    <button
                        onClick={() => setActiveTab('requests')}
                        style={{
                            padding: '10px 16px',
                            border: 'none',
                            borderBottom: activeTab === 'requests' ? '3px solid #3b82f6' : 'none',
                            background: 'transparent',
                            color: activeTab === 'requests' ? '#3b82f6' : '#64748b',
                            cursor: 'pointer',
                            fontWeight: activeTab === 'requests' ? 600 : 500,
                            fontSize: '0.95rem'
                        }}
                    >
                        ⏳ Pending Requests
                    </button>
                    <button
                        onClick={() => setActiveTab('approved')}
                        style={{
                            padding: '10px 16px',
                            border: 'none',
                            borderBottom: activeTab === 'approved' ? '3px solid #3b82f6' : 'none',
                            background: 'transparent',
                            color: activeTab === 'approved' ? '#3b82f6' : '#64748b',
                            cursor: 'pointer',
                            fontWeight: activeTab === 'approved' ? 600 : 500,
                            fontSize: '0.95rem'
                        }}
                    >
                        ✅ Approved & Created
                    </button>
                    <button
                        onClick={() => setActiveTab('history')}
                        style={{
                            padding: '10px 16px',
                            border: 'none',
                            borderBottom: activeTab === 'history' ? '3px solid #3b82f6' : 'none',
                            background: 'transparent',
                            color: activeTab === 'history' ? '#3b82f6' : '#64748b',
                            cursor: 'pointer',
                            fontWeight: activeTab === 'history' ? 600 : 500,
                            fontSize: '0.95rem'
                        }}
                    >
                        📁 All History
                    </button>
                </div>

                {/* Pending Requests */}
                {activeTab === 'requests' && (
                    <div>
                        {loadingList ? (
                            <div className="fc-empty-state">Loading...</div>
                        ) : requests.length === 0 ? (
                            <div className="fc-empty-state">
                                <p>No pending requests.</p>
                                {isApprover && <small>Approvers will see requests here.</small>}
                            </div>
                        ) : (
                            <div className="fc-table-wrap">
                                <table className="fc-table">
                                    <thead>
                                        <tr>
                                            <th>Submitted</th>
                                            <th>Requester</th>
                                            <th>From (US)</th>
                                            <th>To (PK)</th>
                                            <th>Account</th>
                                            <th>Type</th>
                                            <th>Status</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {requests.map((req, idx) => (
                                            <tr key={req._id || idx}>
                                                <td style={{ fontSize: '0.85rem', color: '#64748b' }}>{fmtDate(req.createdAt)}</td>
                                                <td style={{ fontWeight: 600, color: '#1e293b', fontSize: '0.9rem' }}>{req.requesterEmail?.split('@')[0]}</td>
                                                <td>
                                                    <div style={{ fontWeight: 600, color: '#1e293b', fontSize: '0.9rem' }}>{req.shipFrom?.name}</div>
                                                    <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{req.shipFrom?.city}</div>
                                                </td>
                                                <td>
                                                    <div style={{ fontWeight: 600, color: '#1e293b', fontSize: '0.9rem' }}>{req.shipTo?.name}</div>
                                                    <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{req.shipTo?.city}</div>
                                                </td>
                                                <td style={{ fontFamily: 'monospace', fontSize: '0.85rem', color: '#374151' }}>{req.accountNumber}</td>
                                                <td>
                                                    <span className={`fc-type-badge ${req.serviceType === 'NON-DOCUMENTS' ? 'nondoc' : req.serviceType === 'LETTER' ? 'letter' : 'doc'}`}>
                                                        {req.serviceType === 'NON-DOCUMENTS' ? 'Non-Doc' : req.serviceType === 'DOCUMENTS' ? 'Doc' : 'Letter'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span style={{ color: '#f59e0b', fontWeight: 600 }}>⏳ Pending</span>
                                                </td>
                                                <td>
                                                    <button
                                                        onClick={() => { setSelectedRequest(req); setShowDetailModal(true); }}
                                                        style={{
                                                            padding: '6px 12px',
                                                            borderRadius: '4px',
                                                            background: '#dbeafe',
                                                            color: '#0369a1',
                                                            border: 'none',
                                                            cursor: 'pointer',
                                                            fontWeight: 600,
                                                            fontSize: '0.85rem'
                                                        }}
                                                    >
                                                        View
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {/* Approved Requests */}
                {activeTab === 'approved' && (
                    <div>
                        {loadingList ? (
                            <div className="fc-empty-state">Loading...</div>
                        ) : approved.length === 0 ? (
                            <div className="fc-empty-state">
                                <p>No approved and created labels.</p>
                            </div>
                        ) : (
                            <div className="fc-table-wrap">
                                <table className="fc-table">
                                    <thead>
                                        <tr>
                                            <th>Approved</th>
                                            <th>Requester</th>
                                            <th>From (US)</th>
                                            <th>To (PK)</th>
                                            <th>Tracking #</th>
                                            <th>Type</th>
                                            <th>Status</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {approved.map((req, idx) => (
                                            <tr key={req._id || idx}>
                                                <td style={{ fontSize: '0.85rem', color: '#64748b' }}>{fmtDate(req.approvedAt)}</td>
                                                <td style={{ fontWeight: 600, color: '#1e293b', fontSize: '0.9rem' }}>{req.requesterEmail?.split('@')[0]}</td>
                                                <td>
                                                    <div style={{ fontWeight: 600, color: '#1e293b', fontSize: '0.9rem' }}>{req.shipFrom?.name}</div>
                                                    <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{req.shipFrom?.city}</div>
                                                </td>
                                                <td>
                                                    <div style={{ fontWeight: 600, color: '#1e293b', fontSize: '0.9rem' }}>{req.shipTo?.name}</div>
                                                    <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{req.shipTo?.city}</div>
                                                </td>
                                                <td style={{ fontFamily: 'monospace', fontSize: '0.85rem', color: '#4338ca', fontWeight: 700 }}>{req.trackingNumber || '—'}</td>
                                                <td>
                                                    <span className={`fc-type-badge ${req.serviceType === 'NON-DOCUMENTS' ? 'nondoc' : req.serviceType === 'LETTER' ? 'letter' : 'doc'}`}>
                                                        {req.serviceType === 'NON-DOCUMENTS' ? 'Non-Doc' : req.serviceType === 'DOCUMENTS' ? 'Doc' : 'Letter'}
                                                    </span>
                                                </td>
                                                <td>
                                                    {req.status === 'approved' && <span style={{ color: '#10b981', fontWeight: 600 }}>✅ Approved</span>}
                                                    {req.status === 'voided' && <span style={{ color: '#6b7280', fontWeight: 600 }}>🚫 Voided</span>}
                                                </td>
                                                <td style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                                    <button
                                                        onClick={() => { setSelectedRequest(req); setShowDetailModal(true); }}
                                                        style={{
                                                            padding: '6px 12px', borderRadius: '4px',
                                                            background: '#dcfce7', color: '#166534',
                                                            border: 'none', cursor: 'pointer',
                                                            fontWeight: 600, fontSize: '0.85rem'
                                                        }}
                                                    >
                                                        View
                                                    </button>
                                                    {isApprover && req.status === 'approved' && (
                                                        <button
                                                            onClick={() => handleVoidRequest(req._id)}
                                                            disabled={loading}
                                                            title="Void this label with UPS"
                                                            style={{
                                                                padding: '6px 12px', borderRadius: '4px',
                                                                background: '#fee2e2', color: '#991b1b',
                                                                border: 'none', cursor: loading ? 'not-allowed' : 'pointer',
                                                                fontWeight: 600, fontSize: '0.85rem',
                                                                opacity: loading ? 0.6 : 1
                                                            }}
                                                        >
                                                            🚫 Void
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {/* History */}
                {activeTab === 'history' && (
                    <div>
                        {loadingList ? (
                            <div className="fc-empty-state">Loading...</div>
                        ) : history.length === 0 ? (
                            <div className="fc-empty-state">
                                <p>No request history.</p>
                            </div>
                        ) : (
                            <div className="fc-table-wrap">
                                <table className="fc-table">
                                    <thead>
                                        <tr>
                                            <th>Date</th>
                                            <th>Requester</th>
                                            <th>From (US)</th>
                                            <th>To (PK)</th>
                                            <th>Tracking #</th>
                                            <th>Type</th>
                                            <th>Status</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {history.map((req, idx) => (
                                            <tr key={req._id || idx}>
                                                <td style={{ fontSize: '0.85rem', color: '#64748b' }}>{fmtDate(req.createdAt)}</td>
                                                <td style={{ fontWeight: 600, color: '#1e293b', fontSize: '0.9rem' }}>{req.requesterEmail?.split('@')[0]}</td>
                                                <td>
                                                    <div style={{ fontWeight: 600, color: '#1e293b', fontSize: '0.9rem' }}>{req.shipFrom?.name}</div>
                                                    <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{req.shipFrom?.city}</div>
                                                </td>
                                                <td>
                                                    <div style={{ fontWeight: 600, color: '#1e293b', fontSize: '0.9rem' }}>{req.shipTo?.name}</div>
                                                    <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{req.shipTo?.city}</div>
                                                </td>
                                                <td style={{ fontFamily: 'monospace', fontSize: '0.85rem', color: '#4338ca', fontWeight: 700 }}>{req.trackingNumber || '—'}</td>
                                                <td>
                                                    <span className={`fc-type-badge ${req.serviceType === 'NON-DOCUMENTS' ? 'nondoc' : req.serviceType === 'LETTER' ? 'letter' : 'doc'}`}>
                                                        {req.serviceType === 'NON-DOCUMENTS' ? 'Non-Doc' : req.serviceType === 'DOCUMENTS' ? 'Doc' : 'Letter'}
                                                    </span>
                                                </td>
                                                <td>
                                                    {req.status === 'pending' && <span style={{ color: '#f59e0b', fontWeight: 600 }}>⏳ Pending</span>}
                                                    {req.status === 'approved' && <span style={{ color: '#10b981', fontWeight: 600 }}>✅ Approved</span>}
                                                    {req.status === 'rejected' && <span style={{ color: '#ef4444', fontWeight: 600 }}>❌ Rejected</span>}
                                                </td>
                                                <td>
                                                    <button
                                                        onClick={() => { setSelectedRequest(req); setShowDetailModal(true); }}
                                                        style={{
                                                            padding: '6px 12px',
                                                            borderRadius: '4px',
                                                            background: '#f1f5f9',
                                                            color: '#475569',
                                                            border: '1px solid #cbd5e0',
                                                            cursor: 'pointer',
                                                            fontWeight: 600,
                                                            fontSize: '0.85rem'
                                                        }}
                                                    >
                                                        View
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Detail Modal */}
            {showDetailModal && (
                <RequestDetailModal
                    request={selectedRequest}
                    onClose={() => { setShowDetailModal(false); setSelectedRequest(null); }}
                    onApprove={handleApproveRequest}
                    onReject={handleRejectRequest}
                    onVoid={handleVoidRequest}
                    isApprover={isApprover}
                    loading={loading}
                />
            )}
        </div>
    );
};

export default RequestFC;
